import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import CleaningStaffCard from '@/components/v2/molecules/admin/tabs/UserManagementTabs/CleaningStaffTab/CleaningStaffCard';
import PropertyOwnersTabSkeleton from '@/components/v2/molecules/admin/tabs/UserManagementTabs/PropertyOwners/PropertyOwnersTabSkeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useUsers } from '@/hooks/useUsers';
import { useEnrichedTasks } from '@/hooks/admin/AdminTodosTabs/useTasks';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import TabWarningBanner from '../../../../../molecules/admin/shared/TabWarningBanner';
import CreateUserModal from '../../shared/CreateUserModal';
import TaskFormModal from '../../AdminTodosTab/TaskFormModal';

const CleaningStaffTab = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [selectedCleaningStaff, setSelectedCleaningStaff] = useState<any>(null);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);

  const { user, profile } = useAuth();
  const { resendInvitation } = useUserManagement();

  const {
    data: cleaningStaffList = [],
    isLoading: isLoadingCleaningStaff,
    error: errorCleaningStaff,
    refetch: refetchCleaningStaff,
  } = useUsers('cleaning');

  const { data: tasks = [] } = useEnrichedTasks();

  const [taskFormDefaultDate, setTaskFormDefaultDate] = useState<Date | null>(
    null,
  );

  const openCreateModal = () => setShowCreateModal(true);
  const closeCreateModal = () => setShowCreateModal(false);

  const openTaskForm = (cleaningStaff: any) => {
    setSelectedCleaningStaff(cleaningStaff);
    setShowTaskForm(true);
  };

  const closeTaskForm = () => {
    setSelectedCleaningStaff(null);
    setShowTaskForm(false);
  };

  const handleResendInvitation = async (userId: string) => {
    setResendingUserId(userId);
    await resendInvitation(userId);
    setResendingUserId(null);
  };

  const handleCreateUserSuccess = async (createdUserId: string) => {
    closeCreateModal();

    const { data: updatedCleaningStaffList = [] } =
      await refetchCleaningStaff();

    const newStaff = updatedCleaningStaffList.find(
      (staff) => staff.user_id === createdUserId,
    );

    if (newStaff) {
      openTaskForm(newStaff);
    }
  };

  const handleTaskFormSubmit = async () => {
    closeTaskForm();
    refetchCleaningStaff();
  };

  if (!user || !profile || profile.role !== 'super_admin') {
    return (
      <TabWarningBanner
        title="Acceso denegado"
        message="Solo los super administradores pueden acceder a esta sección."
      />
    );
  }

  if (isLoadingCleaningStaff) return <PropertyOwnersTabSkeleton />;

  if (errorCleaningStaff) {
    return (
      <TabErrorBanner
        title="Error al cargar personal de limpieza"
        message="No se pudo cargar el personal de limpieza. Por favor, intenta nuevamente."
        onRetry={() => refetchCleaningStaff()}
      />
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Personal de Limpieza
          </h2>
          <p className="text-gray-600">
            Gestiona el personal de limpieza y sus asignaciones
          </p>
        </div>

        <Button
          className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
          onClick={openCreateModal}
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar Personal
        </Button>
      </div>

      <div className="grid gap-4">
        {cleaningStaffList.map((staff) => (
          <CleaningStaffCard
            key={staff.user_id}
            user={staff}
            tasks={tasks}
            onAssign={openTaskForm}
            onResendInvitation={handleResendInvitation}
            isLoading={resendingUserId === staff.user_id}
          />
        ))}

        {cleaningStaffList.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-600">
                No hay personal de limpieza registrado.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <CreateUserModal
        isOpen={showCreateModal}
        onClose={closeCreateModal}
        onSuccess={handleCreateUserSuccess}
        userRole="cleaning"
      />

      {selectedCleaningStaff && (
        <TaskFormModal
          open={showTaskForm}
          onOpenChange={setShowTaskForm}
          defaultDueDate={taskFormDefaultDate}
          onSubmit={handleTaskFormSubmit}
          initialAssignedUserId={selectedCleaningStaff.user_id || null}
        />
      )}
    </div>
  );
};

export default CleaningStaffTab;
